import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { AppState } from '../../store/states/app.state';
import { CountryState } from '../../store/states/country.state';
import { StoreName } from '../../store/definitions/store.constants';
import * as countryActions from '../../store/actions/country.action';
import { StorageService } from '../../services/storage/storage.service';
import { PO_QUERY_PARAM } from '../components/purchase-order-detail/purchase-order.constants';

/**
 * Reads the `country` query param and, when present and the user has access to that
 * country, sets it (localStorage + ngrx dispatch) before the route component loads.
 *
 * Always returns true — the guard never blocks navigation, it only switches country
 * as a side effect so that the PO is fetched from the correct country's API.
 */
@Injectable()
export class CountryQueryParamGuard implements CanActivate {
  constructor(
    private readonly store: Store<AppState>,
    private readonly storageService: StorageService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> | boolean {
    const countryInParams = route.queryParamMap.get(PO_QUERY_PARAM.COUNTRY);

    if (!countryInParams) {
      return true;
    }

    const normalizedCountry = countryInParams.toUpperCase();

    return this.store.pipe(
      select(StoreName.country),
      take(1),
      map((countryState: CountryState) => {
        const currentCountry = countryState.countrySelected;
        const countriesForUser = this.storageService.getCountries();
        const availableCountry = countriesForUser.find(
          (country) => country.code.toUpperCase() === normalizedCountry && !country.platform_outage,
        );

        if (!!availableCountry && currentCountry !== normalizedCountry) {
          this.storageService.setStorageCountry(normalizedCountry);
          this.store.dispatch(countryActions.set({ payload: normalizedCountry }));
        }

        return true;
      }),
    );
  }
}
